import * as Popover from "@radix-ui/react-popover";
import { Command as CommandPrimitive } from "cmdk";
import * as React from "react";

import { Option } from "../../../../../types/Option";

import { cn } from "../../../../../lib/utils";
import { getOptionKey } from "../../utils/optionKey";

import { DROPDOWN_ANIMATIONS, DROPDOWN_VARIANTS, SCROLL_STYLES } from "../../../../../lib/styles";

import { SelectOptionItem } from "../SelectOptionItem";
import { SelectSearchHeader } from "../SelectSearchHeader";

type MultipleSelectDropdownProps<T> = {
	triggerWidth?: number;
	selectionVariant: "select" | "combobox";
	searchValue: string;
	onSearchChange: (value: string) => void;
	isLoading: boolean;
	filteredOptions: Option<T>[];
	options: Option<T>[];
	value: T[];
	renderOption?: (option: Option<T>, isSelected: boolean) => React.ReactNode;
	isDisabled: boolean;
	onItemSelect: (option: Option<T>) => void;
	fetchOptions?: (searchTerm: string) => Promise<Option<T>[]>;
	searchInputRef: React.RefObject<HTMLInputElement | null>;
	parentRef: React.RefObject<HTMLDivElement | null>;
	virtualizer: {
		getTotalSize: () => number;
		getVirtualItems: () => Array<{ index: number; size: number; start: number }>;
	};
	containerHeight: number;
	focusedIndex: number;
	isKeyboardNavActive: boolean;
	handleKeyDown: (e: React.KeyboardEvent) => void;
	handleItemMouseEnter: (index: number) => void;
	handleItemMouseLeave: () => void;
	virtualizationConfig?: {
		itemHeight?: number;
		maxHeight?: number;
	};
	position?: "top" | "right" | "bottom" | "left";
	isLimitReached?: boolean;
};

export const MultipleSelectDropdown = <T,>({
	triggerWidth,
	selectionVariant,
	searchValue,
	onSearchChange,
	isLoading,
	filteredOptions,
	options,
	value,
	renderOption,
	isDisabled,
	onItemSelect,
	fetchOptions,
	searchInputRef,
	parentRef,
	virtualizer,
	containerHeight,
	focusedIndex,
	isKeyboardNavActive,
	handleKeyDown,
	handleItemMouseEnter,
	handleItemMouseLeave,
	virtualizationConfig,
	position = "bottom",
	isLimitReached = false,
}: MultipleSelectDropdownProps<T>) => {
	return (
		<Popover.Portal container={document.body}>
			<Popover.Content
				className={cn(DROPDOWN_VARIANTS({ hasBorder: true }), DROPDOWN_ANIMATIONS)}
				side={position}
				align="start"
				sideOffset={8}
				avoidCollisions={false}
				style={{
					width: triggerWidth,
					minWidth: triggerWidth,
					zIndex: 9999,
				}}
				onCloseAutoFocus={(e: Event) => e.preventDefault()}
			>
				<CommandPrimitive className="flex w-full flex-col overflow-hidden rounded-1" shouldFilter={false}>
					{selectionVariant === "combobox" && (
						<SelectSearchHeader
							options={options}
							fetchOptions={fetchOptions}
							searchValue={searchValue}
							onSearchChange={onSearchChange}
							searchInputRef={searchInputRef}
							onKeyDown={handleKeyDown}
							parentRef={parentRef}
							isLimitReached={isLimitReached}
						/>
					)}

					<div
						role="listbox"
						aria-label="Options list"
						className={cn(
							"max-h-60 overflow-x-hidden focus:outline-hidden",
							isLimitReached
								? "cursor-not-allowed overflow-y-hidden opacity-disabled [&_*]:pointer-events-none"
								: "overflow-y-auto"
						)}
						aria-disabled={isLimitReached || undefined}
						style={
							selectionVariant === "combobox"
								? {
										...SCROLL_STYLES,
										height: `${containerHeight}px`,
										minHeight: `${containerHeight}px`,
									}
								: SCROLL_STYLES
						}
						ref={parentRef}
						onKeyDown={isLimitReached ? undefined : handleKeyDown}
						tabIndex={isLimitReached ? -1 : 0}
					>
						{isLoading ? (
							<div className="px-3 py-2 text-paragraph-3 text-neutral-5">Loading options...</div>
						) : filteredOptions.length === 0 ? (
							<CommandPrimitive.Empty className="px-3 py-2 text-paragraph-3 text-neutral-5">
								No options available
							</CommandPrimitive.Empty>
						) : selectionVariant === "combobox" ? (
							<CommandPrimitive.Group>
								<div
									style={{
										height: `${virtualizer.getTotalSize()}px`,
										width: "100%",
										position: "relative",
									}}
								>
									{virtualizer
										.getVirtualItems()
										.map((virtualItem: { index: number; size: number; start: number }) => {
											const option = filteredOptions[virtualItem.index];
											if (!option) return null;

											const isSelected = value.includes(option.value);
											const isFocused = isKeyboardNavActive && focusedIndex === virtualItem.index;

											return (
												<SelectOptionItem
													key={getOptionKey(option)}
													option={option}
													isSelected={isSelected}
													isFocused={isFocused}
													onSelect={() => {
														if (!option.disabled && !isDisabled) {
															onItemSelect(option);
														}
													}}
													renderOption={renderOption}
													virtualItem={virtualItem}
													onMouseEnter={() => handleItemMouseEnter(virtualItem.index)}
													onMouseLeave={handleItemMouseLeave}
												/>
											);
										})}
								</div>
							</CommandPrimitive.Group>
						) : (
							<CommandPrimitive.Group>
								{filteredOptions.map((option, index) => {
									const isSelected = value.includes(option.value);
									const isFocused = isKeyboardNavActive && focusedIndex === index;

									return (
										<SelectOptionItem
											key={getOptionKey(option)}
											option={option}
											isSelected={isSelected}
											isFocused={isFocused}
											onSelect={() => {
												if (!option.disabled && !isDisabled) {
													onItemSelect(option);
												}
											}}
											renderOption={renderOption}
											virtualizationConfig={virtualizationConfig}
										/>
									);
								})}
							</CommandPrimitive.Group>
						)}
					</div>
				</CommandPrimitive>
			</Popover.Content>
		</Popover.Portal>
	);
};
